import React from 'react';
import { Icon } from 'Components';
import { OverviewCardProps } from 'LearningManager/components/OverviewCard';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';

export type OverviewCardData = Omit<OverviewCardProps, 'tooltip'> & {
	id: string;
	tooltip: string;
};

interface FilterOption {
	id: number;
	label: string;
}

export const PRIORITY_OPTIONS: FilterOption[] = [
	{ id: 1, label: 'All states' },
	{ id: 2, label: 'Due soon' },
	{ id: 3, label: 'Overdue' },
	{ id: 3, label: 'No due date' }
];

export const STATUS_OPTIONS: FilterOption[] = [
	{ id: 1, label: 'All states' },
	{ id: 2, label: 'Scheduled' },
	{ id: 3, label: 'Not started' },
	{ id: 3, label: 'In progress' },
	{ id: 3, label: 'Completed' }
];

export const TYPE_OPTIONS: FilterOption[] = [
	{ id: 1, label: 'All types' },
	{ id: 2, label: 'Required' },
	{ id: 3, label: 'Optional' }
];

export const CATEGORY_OPTIONS: FilterOption[] = [
	{ id: 1, label: 'All categories' },
	{ id: 2, label: 'Core Essentials' },
	{ id: 3, label: 'Hospitality-Specific' },
	{ id: 4, label: 'AI Driven' }
];

export interface CourseData {
	id: number;
	title: string;
	meta: string;
	tags: string[];
	image: string;
}

export type LearningPathStatus = 'Required' | 'Scheduled' | 'In Progress' | 'Complete';

export interface LearningPathGroup {
	id: string;
	label: string;
	courses: CourseData[];
}

export interface LearningPathData {
	id: string;
	title: string;
	statuses: string[];
	courseCount: number;
	completedCount: number;
	completionPercentage: number;
	dueDate?: string;
	startsDate?: string;
	courses: CourseData[];
	learningPaths?: LearningPathGroup[];
	individualCourses?: CourseData[];
}

export const LEARNING_PATHS_DATA: LearningPathData[] = [
	{
		id: 'lp-a',
		title: '[Learning Path A]',
		statuses: ['Required', 'Scheduled'],
		courseCount: 3,
		completedCount: 0,
		completionPercentage: 25,
		dueDate: '12/25/2025',
		courses: [
			{ id: 1, title: 'Introduction to Data Privacy', meta: '30 min | Due 12/25/2025', tags: ['Not started', 'Required'], image: course1 },
			{ id: 2, title: 'Hotel Booking Scam Awareness', meta: '15 min | Due 12/25/2025', tags: ['Not started', 'Required'], image: course2 },
			{ id: 3, title: 'GDPR Compliance Training', meta: '12 min | Due 12/25/2025', tags: ['Not started', 'Required'], image: course3 }
		]
	},
	{
		id: 'lp-b',
		title: '[Learning Path B]',
		statuses: ['In Progress'],
		courseCount: 4,
		completedCount: 2,
		completionPercentage: 65,
		courses: [
			{ id: 1, title: 'Privacy Policy Overview', meta: '13 min | Due 12/25/2025', tags: ['Not started', 'Required'], image: course4 },
			{ id: 2, title: 'Data Handling Best Practices', meta: '20 min | Due 12/25/2025', tags: ['Not started', 'Required'], image: course1 },
			{ id: 3, title: 'Privacy Fundamentals', meta: '20 min | Completed', tags: ['Complete', 'Required'], image: course2 },
			{ id: 4, title: 'Consumer Privacy Rights', meta: '5 min | Completed', tags: ['Complete', 'Required'], image: course3 }
		]
	},
	{
		id: 'lp-c',
		title: '[Learning Path C]',
		statuses: ['Complete'],
		courseCount: 2,
		completedCount: 2,
		completionPercentage: 100,
		dueDate: '12/25/2025',
		courses: [
			{ id: 1, title: 'Information Security Breach', meta: '18 min | Completed', tags: ['Complete', 'Required'], image: course4 },
			{ id: 2, title: 'Workplace Ethics', meta: '8 min | Completed', tags: ['Complete', 'Required'], image: course1 }
		]
	},
	{
		id: 'lp-d',
		title: '[Learning Path D]',
		statuses: ['Scheduled'],
		courseCount: 3,
		completedCount: 0,
		completionPercentage: 20,
		startsDate: '12/25/2025',
		courses: [
			{ id: 1, title: 'Harassment Prevention', meta: '9 min | Starts 12/25/2025', tags: ['Not started', 'Required'], image: course2 },
			{ id: 2, title: 'Advanced Security Training', meta: '15 min | Starts 12/25/2025', tags: ['Not started', 'Required'], image: course3 },
			{ id: 3, title: 'Personal Data Management', meta: '14 min | Starts 12/25/2025', tags: ['Not started', 'Required'], image: course4 }
		]
	}
];

export interface CourseGroup {
	scope: string;
	label: string;
	courses: CourseData[];
}

export const COURSE_GROUPS_DATA: CourseGroup[] = [
	{
		scope: 'dp',
		label: 'Data Protection',
		courses: [
			{
				id: 1,
				title: 'Introduction to Data Privacy',
				meta: '30 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course1
			},
			{
				id: 2,
				title: 'Hotel Booking Scam Awareness',
				meta: '15 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course2
			},
			{
				id: 3,
				title: 'GDPR Compliance Training',
				meta: '12 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course3
			},
			{
				id: 4,
				title: 'Privacy Policy Overview',
				meta: '13 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course4
			},
			{
				id: 5,
				title: 'Data Handling Best Practices',
				meta: '20 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course1
			}
		]
	},
	{
		scope: 'privacy',
		label: 'Privacy',
		courses: [
			{
				id: 1,
				title: 'Privacy Fundamentals',
				meta: '20 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course2
			},
			{
				id: 2,
				title: 'Personal Data Management',
				meta: '14 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course1
			},
			{
				id: 3,
				title: 'Consumer Privacy Rights',
				meta: '5 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course4
			},
			{
				id: 4,
				title: 'Privacy in the Workplace',
				meta: '14 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course3
			},
			{
				id: 5,
				title: 'Information Security Breach',
				meta: '18 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course2
			}
		]
	},
	{
		scope: 'hr',
		label: 'Human Resources',
		courses: [
			{
				id: 1,
				title: 'Workplace Ethics',
				meta: '8 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course1
			},
			{
				id: 2,
				title: 'Harassment Prevention',
				meta: '9 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course2
			},
			{
				id: 3,
				title: 'Advanced Security Training',
				meta: '15 min | Due 12/12/2025',
				tags: ['Not started', 'Required'],
				image: course3
			}
		]
	}
];

export const OVERVIEW_CARDS_DATA: OverviewCardData[] = [
	{
		id: 'overall-completion',
		value: '77%',
		label: 'Overall Completion',
		description: 'Required courses only',
		icon: <Icon type="warningCubeIcon" role="img" />,
		tooltip:
			'Percentage of required courses completed. This metric focuses only on mandatory training.'
	},
	{
		id: 'courses-remaining',
		value: 10,
		label: 'Course Remaining',
		description: 'Required courses only',
		icon: <Icon type="grayCourseIcon" role="img" />,
		tooltip:
			'Number of required courses you still need to complete to meet your training obligations.'
	},
	{
		id: 'courses-complete',
		value: 33,
		label: 'Courses Complete',
		description: 'All finished training',
		icon: <Icon type="tickBoxIcon" role="img" />,
		tooltip:
			'Total number of courses you have successfully completed across all learning paths.'
	},
	{
		id: 'optional-course',
		value: 1,
		label: 'Optional Course',
		description: 'Remaining to complete',
		icon: <Icon type="optionalCourseIcon" role="img" />,
		tooltip:
			'Additional courses to enhance your skills and career development. Not required but recommended.'
	}
];
